/**
 * DealContacts — manage the set of contacts associated with a deal.
 *
 * A deal can have several contacts; exactly one is the *primary* (mirrors
 * deals.contact_id). Add via a searchable combobox, make-primary, or remove.
 * Names are resolved from the Holder's contact list (already loaded), so
 * this only deals in ids + role + isPrimary from the deal-contacts endpoint.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Star, X } from 'lucide-react';
import { ARCHE_PATH } from '../constants';
import {
  useFiasLeadsContacts,
  useFiasLeadsDealContacts,
  useAddFiasLeadsDealContact,
  useRemoveFiasLeadsDealContact,
  useSetFiasLeadsDealContactPrimary,
} from '../hooks/useFiasLeads';
import type { FiasLeadsContactDto } from '@/lib/api';
import { ContactCombobox } from './ContactCombobox';

function nameFor(c: FiasLeadsContactDto | undefined, fallback: string): string {
  if (!c) return fallback;
  const name = `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim();
  return name || c.primaryEmail || fallback;
}

export function DealContacts({ holderId, dealId }: { holderId: string; dealId: string }) {
  const contactsQuery = useFiasLeadsContacts(holderId);
  const dealContactsQuery = useFiasLeadsDealContacts(holderId, dealId);
  const addContact = useAddFiasLeadsDealContact(holderId);
  const removeContact = useRemoveFiasLeadsDealContact(holderId);
  const setPrimary = useSetFiasLeadsDealContactPrimary(holderId);

  const allContacts = contactsQuery.data ?? [];
  const dealContacts = dealContactsQuery.data ?? [];

  const byId = useMemo(() => {
    const m = new Map<string, FiasLeadsContactDto>();
    for (const c of allContacts) m.set(c.contactId, c);
    return m;
  }, [allContacts]);

  const memberIds = dealContacts.map((d) => d.contactId);
  const busy = addContact.isPending || removeContact.isPending || setPrimary.isPending;

  const handleAdd = async (contactId: string) => {
    try {
      await addContact.mutateAsync({ dealId, contactId });
    } catch (err) {
      alert(`Could not add contact: ${(err as Error).message}`);
    }
  };

  return (
    <div className="rounded border border-border bg-card p-4">
      <h2 className="text-sm font-medium text-muted-foreground mb-3">
        Contacts ({dealContacts.length})
      </h2>

      {dealContacts.length === 0 ? (
        <p className="text-sm text-muted-foreground mb-3">No contacts on this deal yet.</p>
      ) : (
        <ul className="divide-y divide-border mb-3">
          {dealContacts.map((dc) => {
            const c = byId.get(dc.contactId);
            return (
              <li key={dc.contactId} className="py-2 flex items-center gap-2 text-sm">
                {dc.isPrimary ? (
                  <span
                    className="inline-flex items-center gap-1 text-amber-600"
                    title="Primary contact"
                  >
                    <Star className="h-4 w-4 fill-amber-400" />
                  </span>
                ) : (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setPrimary.mutateAsync({ dealId, contactId: dc.contactId })}
                    className="text-muted-foreground hover:text-amber-600 disabled:opacity-40"
                    title="Make primary"
                    aria-label="Make primary contact"
                  >
                    <Star className="h-4 w-4" />
                  </button>
                )}
                <Link
                  to={`${ARCHE_PATH}/contacts/${dc.contactId}`}
                  className="flex-1 hover:underline"
                >
                  {nameFor(c, dc.contactId)}
                  {dc.isPrimary && (
                    <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-700">
                      primary
                    </span>
                  )}
                </Link>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => removeContact.mutateAsync({ dealId, contactId: dc.contactId })}
                  className="text-muted-foreground hover:text-destructive disabled:opacity-40"
                  title="Remove from deal"
                  aria-label={`Remove ${nameFor(c, dc.contactId)} from deal`}
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <ContactCombobox
        contacts={allContacts}
        excludeIds={memberIds}
        onSelect={handleAdd}
        disabled={busy}
        placeholder="Add a contact…"
      />
    </div>
  );
}
